import { DateInputBlock } from '@coast/schemas';
import { isNil } from 'lodash';
import { isDate } from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { DateField } from '@shared/data/entity/fields/DateField';
import { EntityBatchField } from '@shared/data/entity/fields/EntityBatchField';
import { DateEditField } from '@shared/scenes/entity/components/fields/DateEditField';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isDateComponent } from '@shared/scenes/workflows/utils';

interface NoCodeInputBlockDateProps {
  block: DateInputBlock;
  workflowTemplateId: string;
}

export function NoCodeInputBlockDate({ workflowTemplateId, block }: NoCodeInputBlockDateProps) {
  const { values: fields, setFieldValue } = useContext(CardFormContext);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const component = workflowTemplate?.components
    ?.filter(isDateComponent)
    .find(c => c.id === block.name);

  const dateData = fields[block.name];
  const field = useMemo(() => {
    const dateString = isDate(dateData) ? dateData.toISOString() : dateData;
    return DateField.fromFieldJson(dateString);
  }, [dateData]);

  const entityBatchField = useMemo(() => {
    return component?.allowEntityBatchCreation
      ? EntityBatchField.fromFieldJson(fields.entityBatch, null)
      : undefined;
  }, [component, fields.entityBatch]);

  const updateField = useCallback(
    (componentId: string, newDate: DateField | null) => {
      setFieldValue(componentId, !isNil(newDate) ? newDate?.date : null);
    },
    [setFieldValue],
  );

  if (isNil(component)) {
    return null;
  }
  return (
    <DateEditField
      component={component}
      field={field}
      entityBatchField={entityBatchField ?? undefined}
      updateField={updateField}
      workflowTemplateId={workflowTemplateId}
      includeWrapperPadding={false}
    />
  );
}
